/**
 * scanQueue — Dexie-backed offline queue for the universal check-in scanner.
 *
 * Why this exists
 * ---------------
 * Check-in kiosks lose network at conferences (patchy WiFi, LTE dead
 * spots, VPN drops). Before iter-434-Phase-4 a network drop meant scans
 * fell on the floor and the operator had to re-queue delegates manually.
 *
 * This module keeps every scan the operator captures — GREEN, AMBER, or
 * network-failed — in IndexedDB. Once connectivity is back, `flush()`
 * drains them through `POST /api/qr/bulk-sync`, which is idempotent by
 * `client_scan_id` so replays are safe.
 *
 * Public API
 * ----------
 *   const scanQueue = getScanQueue();
 *   await scanQueue.add({ event_id, qr, gate });
 *   const pending = await scanQueue.count();
 *   const summary = await scanQueue.flush(apiUrl, authHeader);
 *
 * Storage schema
 * --------------
 *   entries: {
 *     ++id, client_scan_id, event_id, qr, gate,
 *     scanned_at,           // ISO
 *     status,               // 'pending' | 'applied' | 'duplicate' | 'failed'
 *     verdict, reason,      // filled in after sync
 *     last_error,           // if failed
 *     attempts              // int
 *   }
 */
import Dexie from 'dexie';

const DB_NAME = 'fwm-scan-queue-v1';

class ScanQueueDB extends Dexie {
  constructor() {
    super(DB_NAME);
    this.version(1).stores({
      entries: '++id, client_scan_id, event_id, status, scanned_at',
    });
  }
}

let _instance = null;

function _uuid() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  // Fallback (older Safari) — RFC-4122-ish
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

export function getScanQueue() {
  if (_instance) return _instance;
  const db = new ScanQueueDB();

  const api = {
    /**
     * Enqueue a scan. Returns the generated `client_scan_id`.
     * Doubles as offline log — the row is kept after sync (status=applied).
     */
    async add({ event_id, qr, gate = 'main' }) {
      const client_scan_id = _uuid();
      await db.entries.add({
        client_scan_id, event_id, qr, gate,
        scanned_at: new Date().toISOString(),
        status: 'pending',
        attempts: 0,
      });
      return client_scan_id;
    },

    /** Count of scans still pending sync (`status='pending'`). */
    async count() {
      return db.entries.where('status').equals('pending').count();
    },

    /** All queue rows (any status), newest first. */
    async list(limit = 50) {
      return db.entries.orderBy('id').reverse().limit(limit).toArray();
    },

    /** Wipe *applied* + *duplicate* rows older than N days.  Called on
     *  successful flush so the queue doesn't grow forever. */
    async purgeOld(days = 7) {
      const cutoff = new Date(Date.now() - days * 24 * 3600 * 1000).toISOString();
      return db.entries
        .where('scanned_at').below(cutoff)
        .and(x => ['applied', 'duplicate'].includes(x.status))
        .delete();
    },

    /**
     * Drain the queue via POST /api/qr/bulk-sync.
     * Chunks in batches of `chunk` (default 50) so we stay under the
     * server cap (100).
     *
     * Returns { attempted, applied, duplicate, failed, summary }.
     */
    async flush(apiUrl, authHeader, { chunk = 50 } = {}) {
      const pending = await db.entries.where('status').equals('pending').toArray();
      const summary = { attempted: pending.length, applied: 0, duplicate: 0, failed: 0 };
      if (!pending.length) return summary;

      for (let i = 0; i < pending.length; i += chunk) {
        const slice = pending.slice(i, i + chunk);
        const body = {
          entries: slice.map(e => ({
            client_scan_id: e.client_scan_id,
            event_id: e.event_id,
            qr: e.qr,
            gate: e.gate,
            scanned_at: e.scanned_at,
          })),
        };
        let resJson;
        try {
          const res = await fetch(`${apiUrl}/api/qr/bulk-sync`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(authHeader || {}),
            },
            body: JSON.stringify(body),
          });
          resJson = await res.json().catch(() => null);
          if (!res.ok) {
            // Whole batch failed → mark every row failed (increment attempts)
            await db.transaction('rw', db.entries, async () => {
              for (const e of slice) {
                await db.entries.update(e.id, {
                  status: 'failed',
                  attempts: (e.attempts || 0) + 1,
                  last_error: resJson?.detail || res.statusText,
                });
                summary.failed += 1;
              }
            });
            continue;
          }
        } catch (netErr) {
          // Still offline — leave rows in pending
          await db.transaction('rw', db.entries, async () => {
            for (const e of slice) {
              await db.entries.update(e.id, {
                attempts: (e.attempts || 0) + 1,
                last_error: netErr.message,
              });
              summary.failed += 1;
            }
          });
          continue;
        }
        // Batch response — update each row
        const byCsid = {};
        for (const r of resJson?.results || []) byCsid[r.client_scan_id] = r;
        await db.transaction('rw', db.entries, async () => {
          for (const e of slice) {
            const r = byCsid[e.client_scan_id];
            if (!r) continue;
            const patch = {
              status: r.status,          // 'applied' | 'duplicate' | 'failed'
              verdict: r.verdict || r.original_verdict,
              reason: r.reason,
              attempts: (e.attempts || 0) + 1,
              last_error: r.error || null,
              audit_id: r.audit_id || null,
            };
            await db.entries.update(e.id, patch);
            if (r.status === 'applied') summary.applied += 1;
            else if (r.status === 'duplicate') summary.duplicate += 1;
            else summary.failed += 1;
          }
        });
      }
      // House-keeping
      await api.purgeOld(7).catch(() => {});
      return summary;
    },

    /** Delete a single row (rare — for manual conflict resolution). */
    async remove(id) { return db.entries.delete(id); },
  };

  _instance = api;
  return api;
}

export default getScanQueue;
